import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { uidSchema, type Team } from '@/core/schemas';
import { areSettingsEditable } from '@/core/schemas';
import { useAuth } from '@/app/providers/auth/authContext';
import { describeError } from '@/features/team/describeError';
import { useTournamentData } from '@/features/tournament/useTournamentData';
import {
  deleteTeamAsDirector,
  setTeamSeed,
  setTeamStatus,
} from '@/infrastructure/repositories/teamRepository';
import { usePageTitle } from '@/shared/hooks/usePageTitle';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';
import { PageState } from '@/shared/ui/PageState';

export function AdminTeamsPage() {
  usePageTitle('Teams');
  const { session } = useAuth();
  const { tournament, teams, isLoading, errorMessage } = useTournamentData();
  const [toDelete, setToDelete] = useState<Team | null>(null);

  const actorUid = session.status === 'authenticated' ? uidSchema.parse(session.account.uid) : null;

  const seed = useMutation({
    mutationFn: (input: { team: Team; seed: number | null }) => {
      if (actorUid === null) throw new Error('Nicht angemeldet');
      return setTeamSeed({ team: input.team, seed: input.seed, actorUid });
    },
  });

  const status = useMutation({
    mutationFn: (input: { team: Team; withdraw: boolean }) => {
      if (actorUid === null) throw new Error('Nicht angemeldet');
      return setTeamStatus({
        team: input.team,
        status: input.withdraw ? 'withdrawn' : 'active',
        actorUid,
      });
    },
  });

  const remove = useMutation({
    mutationFn: (team: Team) => {
      if (actorUid === null) throw new Error('Nicht angemeldet');
      return deleteTeamAsDirector({ team, actorUid });
    },
    onSuccess: () => setToDelete(null),
  });

  if (isLoading) {
    return <LoadingScreen label="Teams werden geladen …" />;
  }

  if (errorMessage !== null || tournament === undefined) {
    return <PageState title="Teams nicht verfügbar" description={errorMessage ?? ''} />;
  }

  const canSeed = areSettingsEditable(tournament.status);
  const activeCount = teams.filter((team) => team.status === 'active').length;
  const withdrawnCount = teams.length - activeCount;
  const error =
    describeError(seed.error) ?? describeError(status.error) ?? describeError(remove.error);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-lg font-semibold">
          Teams ({activeCount}
          {withdrawnCount > 0 ? ` aktiv, ${withdrawnCount} zurückgezogen` : ''})
        </h2>
      </div>

      {error !== null && <Alert tone="danger">{error}</Alert>}

      {teams.length === 0 ? (
        <Alert tone="info">Es sind noch keine Teams angemeldet.</Alert>
      ) : (
        <ul className="flex flex-col gap-2">
          {teams.map((team) => {
            const isWithdrawn = team.status === 'withdrawn';
            return (
              <li
                key={team.id}
                className={
                  isWithdrawn
                    ? 'border-line bg-surface-overlay flex flex-wrap items-center gap-3 rounded-lg border p-3 opacity-70'
                    : 'border-line bg-surface flex flex-wrap items-center gap-3 rounded-lg border p-3'
                }
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">
                    {team.name}
                    {isWithdrawn && (
                      <span className="text-fg-subtle ml-2 text-xs font-normal tracking-wide uppercase">
                        zurückgezogen
                      </span>
                    )}
                  </p>
                  <p className="text-fg-subtle truncate text-xs">{team.fireDepartment}</p>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <span className="text-fg-subtle text-xs uppercase">Setzplatz</span>
                  <input
                    type="number"
                    min={1}
                    defaultValue={team.seed ?? ''}
                    disabled={!canSeed || isWithdrawn || seed.isPending}
                    onBlur={(event) => {
                      const value = event.target.value.trim();
                      const next = value === '' ? null : Number(value);
                      if (next !== team.seed) {
                        seed.mutate({ team, seed: next });
                      }
                    }}
                    className="bg-surface-raised border-line w-16 rounded-lg border px-2 py-2 text-center disabled:opacity-50"
                  />
                </label>

                <Button
                  variant="ghost"
                  size="md"
                  onClick={() => status.mutate({ team, withdraw: !isWithdrawn })}
                  isLoading={status.isPending && status.variables.team.id === team.id}
                >
                  {isWithdrawn ? 'Reaktivieren' : 'Zurückziehen'}
                </Button>

                <Button
                  variant="ghost"
                  size="md"
                  onClick={() => setToDelete(team)}
                  className="text-danger"
                >
                  Löschen
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {!canSeed && (
        <p className="text-fg-subtle text-sm">
          Setzpositionen lassen sich nur vor dem Turnierstart ändern. „Zurückziehen" bleibt
          jederzeit möglich; laufende Spiele des Teams müssen als Walkover korrigiert werden.
        </p>
      )}

      <ConfirmDialog
        open={toDelete !== null}
        title="Team löschen?"
        description={
          toDelete === null
            ? ''
            : `„${toDelete.name}" wird endgültig entfernt. Der Teamname und der Zugang des Kapitäns werden wieder frei.`
        }
        confirmLabel="Team löschen"
        tone="danger"
        isBusy={remove.isPending}
        onConfirm={() => {
          if (toDelete !== null) remove.mutate(toDelete);
        }}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
}
